/**
 * What build am I looking at, and what changed in it.
 *
 * The version is a build-time constant (see vite.config.ts). The changelog body still has to be
 * fetched — it is far too big to inline — so the parser below is the part that has to be right.
 *
 * **Why this file exists rather than a regex inside a component.** The heading parse has now failed
 * in production three times, and every time the symptom was identical and uninformative: an em-dash
 * where a version should be and an empty panel. The file is served correctly and always was. Pulled
 * out here it is a pure function over a string, which the smoke test can hold real CRLF bytes
 * against.
 */

/** Empty in a dev tree with no generated notes; the fetch fallback fills it in then. */
export const APP_VERSION: string =
  (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '') || ''

export interface Release {
  version: string
  date: string
  changes: string[]
}

/**
 * Read the generated markdown.
 *
 * Deliberately not a markdown library: one script writes this file, so it has exactly one shape,
 * and a parser dependency would outweigh the content. Anything unparseable is skipped rather than
 * rendered raw.
 *
 * **Split on `/\r?\n/`, and never anchor with `$`.** `release.ps1` runs on Windows and writes CRLF.
 * Splitting on `'\n'` alone leaves a trailing `\r` on every line, and in JavaScript `\r` is a line
 * terminator — so `.` does not match it and `$` (without the `m` flag) means end-of-*string*. A
 * heading regex ending `(.+)$` therefore matched nothing at all, on a file that was completely
 * well-formed. The date is captured separately and optionally for the same reason: it is
 * decoration, and it must never be able to fail the version out of existence.
 */
export function parseReleaseNotes(markdown: string): Release[] {
  const releases: Release[] = []
  let current: Release | null = null

  for (const line of markdown.split(/\r?\n/)) {
    // Same shape as the build-time regex in vite.config.ts: version first, everything else optional.
    const heading = line.match(/^##\s+(v[\d.]+)\s*(?:[-–—(]\s*(.*?)\)?\s*)?$/)
    if (heading) {
      current = { version: heading[1], date: (heading[2] ?? '').trim(), changes: [] }
      releases.push(current)
      continue
    }

    if (current && line.startsWith('- ')) {
      current.changes.push(line.slice(2).trim())
    }
  }

  // Newest first is how the file is written; only the most recent handful is worth showing.
  return releases.slice(0, 10)
}
